import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    LogOut,
    Loader2,
    Mail,
    Phone,
    Briefcase,
    Bot,
    MessageSquare,
    Search,
    X,
    User,
    Building2,
    Calendar,
    Tag,
    Wallet,
    Clock,
    ArrowLeft,
} from "lucide-react";
import { adminGet, adminLogout, getToken, setToken } from "@/lib/adminApi";
import { BRAND } from "@/data/content";

const TABS = [
    { id: "leads", label: "Leads", icon: Briefcase },
    { id: "contacts", label: "Messages", icon: Mail },
    { id: "chats", label: "Chats", icon: Bot },
];

export default function AdminDashboardPage() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("leads");
    const [stats, setStats] = useState(null);
    const [data, setData] = useState({ leads: null, contacts: null, chats: null });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [chatDetail, setChatDetail] = useState(null);
    const [chatLoading, setChatLoading] = useState(false);

    // Auth gate
    useEffect(() => {
        if (!getToken()) {
            navigate("/admin", { replace: true });
            return;
        }
        // Verify token is valid
        adminGet("/me").catch(() => {
            setToken("");
            navigate("/admin", { replace: true });
        });
    }, [navigate]);

    // Initial load
    useEffect(() => {
        if (!getToken()) return;
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                const [s, l, c, ch] = await Promise.all([
                    adminGet("/stats"),
                    adminGet("/leads"),
                    adminGet("/contacts"),
                    adminGet("/chats"),
                ]);
                if (!alive) return;
                setStats(s.data);
                setData({ leads: l.data, contacts: c.data, chats: ch.data });
            } catch (e) {
                if (e?.response?.status === 401) {
                    setToken("");
                    navigate("/admin", { replace: true });
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [navigate]);

    const onLogout = async () => {
        try {
            await adminLogout();
        } catch {
            /* ignore */
        }
        setToken("");
        navigate("/admin", { replace: true });
    };

    const list = data[tab] || [];
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return list;
        const matches = (str) => (str || "").toString().toLowerCase().includes(q);
        if (tab === "leads")
            return list.filter(
                (l) =>
                    matches(l.email) ||
                    matches(l.name) ||
                    matches(l.company) ||
                    matches(l.what_building) ||
                    matches(l.goals)
            );
        if (tab === "contacts")
            return list.filter(
                (c) =>
                    matches(c.email) ||
                    matches(c.name) ||
                    matches(c.subject) ||
                    matches(c.message)
            );
        if (tab === "chats")
            return list.filter(
                (c) => matches(c.session_id) || matches(c.first_message)
            );
        return list;
    }, [list, search, tab]);

    const openChat = async (sessionId) => {
        setChatDetail({ session_id: sessionId, messages: null });
        setChatLoading(true);
        try {
            const { data: msgs } = await adminGet(`/chats/${sessionId}`);
            setChatDetail({ session_id: sessionId, messages: msgs });
        } catch {
            setChatDetail({ session_id: sessionId, messages: [] });
        } finally {
            setChatLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--bg))] flex flex-col" data-testid="admin-dashboard">
            {/* Header */}
            <header className="sticky top-0 z-30 border-b border-black/[0.08] bg-white/85 backdrop-blur">
                <div className="mx-auto max-w-7xl container-px h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <img
                            src={BRAND.logoUrl}
                            alt="IdeaClap"
                            className="h-10 w-10 object-contain"
                        />
                        <div className="flex items-baseline gap-2 min-w-0">
                            <span className="font-display text-xl text-[hsl(var(--text))] tracking-[-0.01em]">
                                IdeaClap
                            </span>
                            <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                Admin
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onLogout}
                        data-testid="admin-logout"
                        className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-black/10 hover:bg-[hsl(var(--bg-2))] px-3.5 py-1.5 text-[12.5px] text-[hsl(var(--text-muted))] transition-colors"
                    >
                        <LogOut className="h-3.5 w-3.5" />
                        Sign out
                    </button>
                </div>
            </header>

            <main className="flex-1">
                <div className="mx-auto max-w-7xl container-px py-8 lg:py-10">
                    {/* Stats row */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        <StatTile
                            label="Project leads"
                            value={stats?.leads ?? "—"}
                            icon={Briefcase}
                            accent="gold"
                        />
                        <StatTile
                            label="Contact messages"
                            value={stats?.contacts ?? "—"}
                            icon={Mail}
                            accent="blue"
                        />
                        <StatTile
                            label="Chat sessions"
                            value={stats?.chat_sessions ?? "—"}
                            icon={Bot}
                            accent="blue"
                        />
                        <StatTile
                            label="Chat messages"
                            value={stats?.chat_messages ?? "—"}
                            icon={MessageSquare}
                            accent="gold"
                        />
                    </div>

                    {/* Tabs + search */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                        <div
                            role="tablist"
                            className="inline-flex items-center gap-1 rounded-full bg-white ring-1 ring-black/[0.08] p-1"
                        >
                            {TABS.map((t) => {
                                const Icon = t.icon;
                                const active = tab === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => {
                                            setTab(t.id);
                                            setSearch("");
                                            setSelected(null);
                                        }}
                                        data-testid={`admin-tab-${t.id}`}
                                        className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-medium transition-colors ${
                                            active
                                                ? "bg-[hsl(var(--text))] text-[hsl(var(--bg))]"
                                                : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                                        }`}
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--text-subtle))]" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search…"
                                className="w-full sm:w-64 rounded-full bg-white ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[13px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] pl-9 pr-3 py-2 outline-none"
                                data-testid="admin-search"
                            />
                        </div>
                    </div>

                    {/* Body */}
                    {loading ? (
                        <div className="flex items-center justify-center py-24 text-[hsl(var(--text-subtle))]">
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            Loading…
                        </div>
                    ) : tab === "leads" ? (
                        <LeadsTable items={filtered} onSelect={setSelected} />
                    ) : tab === "contacts" ? (
                        <ContactsTable items={filtered} onSelect={setSelected} />
                    ) : (
                        <ChatsTable items={filtered} onOpen={openChat} />
                    )}
                </div>
            </main>

            {/* Detail drawer for leads/contacts */}
            <AnimatePresence>
                {selected && (
                    <DetailDrawer
                        kind={tab}
                        record={selected}
                        onClose={() => setSelected(null)}
                    />
                )}
            </AnimatePresence>

            {/* Chat session viewer */}
            <AnimatePresence>
                {chatDetail && (
                    <ChatSessionDrawer
                        sessionId={chatDetail.session_id}
                        messages={chatDetail.messages}
                        loading={chatLoading}
                        onClose={() => setChatDetail(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

// ---------- Subcomponents ----------
const StatTile = ({ label, value, icon: Icon, accent }) => (
    <div className="rounded-2xl border border-black/[0.08] bg-white p-5 shadow-soft">
        <div className="flex items-center justify-between">
            <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                {label}
            </span>
            <span
                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1 ${
                    accent === "gold"
                        ? "bg-[rgba(187,118,33,0.10)] ring-[rgba(187,118,33,0.35)] text-[hsl(var(--gold))]"
                        : "bg-[rgba(37,99,235,0.10)] ring-[rgba(37,99,235,0.35)] text-[hsl(var(--blue))]"
                }`}
            >
                <Icon className="h-3.5 w-3.5" />
            </span>
        </div>
        <div className="font-display text-3xl text-[hsl(var(--text))] mt-2 num-tabular leading-none">
            {value}
        </div>
    </div>
);

const LeadsTable = ({ items, onSelect }) => {
    if (!items.length)
        return <EmptyState title="No leads yet" hint="New project intake submissions will appear here." />;
    return (
        <div className="rounded-2xl border border-black/[0.08] bg-white overflow-hidden shadow-soft">
            <table className="min-w-full text-[13.5px]">
                <thead className="bg-[hsl(var(--bg-2))]/70 text-[hsl(var(--text-subtle))] text-[11px] font-mono uppercase tracking-[0.16em]">
                    <tr>
                        <th className="text-left font-medium px-4 py-3">Date</th>
                        <th className="text-left font-medium px-4 py-3">Email</th>
                        <th className="text-left font-medium px-4 py-3">Company</th>
                        <th className="text-left font-medium px-4 py-3">Building</th>
                        <th className="text-left font-medium px-4 py-3">Capabilities</th>
                        <th className="text-left font-medium px-4 py-3">Timeline</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((l) => (
                        <tr
                            key={l.id}
                            onClick={() => onSelect(l)}
                            data-testid="admin-lead-row"
                            className="border-t border-black/[0.06] hover:bg-[hsl(var(--bg-2))]/60 cursor-pointer transition-colors"
                        >
                            <td className="px-4 py-3 text-[hsl(var(--text-muted))] whitespace-nowrap">
                                {fmtDate(l.created_at)}
                            </td>
                            <td className="px-4 py-3 text-[hsl(var(--text))]">{l.email}</td>
                            <td className="px-4 py-3 text-[hsl(var(--text-muted))]">
                                {l.company || "—"}
                            </td>
                            <td className="px-4 py-3 text-[hsl(var(--text-muted))] max-w-[280px] truncate">
                                {l.what_building}
                            </td>
                            <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                    {(l.capabilities || []).map((c) => (
                                        <span
                                            key={c}
                                            className="inline-block rounded-full bg-[rgba(187,118,33,0.10)] text-[hsl(var(--gold))] px-2 py-0.5 text-[10.5px] font-mono uppercase tracking-[0.12em]"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            </td>
                            <td className="px-4 py-3 text-[hsl(var(--text-muted))] text-[12.5px] whitespace-nowrap">
                                {l.timeline || "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ContactsTable = ({ items, onSelect }) => {
    if (!items.length)
        return <EmptyState title="No messages yet" hint="Contact form submissions will appear here." />;
    return (
        <div className="rounded-2xl border border-black/[0.08] bg-white overflow-hidden shadow-soft">
            <table className="min-w-full text-[13.5px]">
                <thead className="bg-[hsl(var(--bg-2))]/70 text-[hsl(var(--text-subtle))] text-[11px] font-mono uppercase tracking-[0.16em]">
                    <tr>
                        <th className="text-left font-medium px-4 py-3">Date</th>
                        <th className="text-left font-medium px-4 py-3">From</th>
                        <th className="text-left font-medium px-4 py-3">Subject</th>
                        <th className="text-left font-medium px-4 py-3">Message</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((c) => (
                        <tr
                            key={c.id}
                            onClick={() => onSelect(c)}
                            data-testid="admin-contact-row"
                            className="border-t border-black/[0.06] hover:bg-[hsl(var(--bg-2))]/60 cursor-pointer transition-colors"
                        >
                            <td className="px-4 py-3 text-[hsl(var(--text-muted))] whitespace-nowrap">
                                {fmtDate(c.created_at)}
                            </td>
                            <td className="px-4 py-3 text-[hsl(var(--text))]">
                                <div className="flex flex-col">
                                    <span>{c.name || "—"}</span>
                                    <span className="text-[12px] text-[hsl(var(--text-subtle))]">
                                        {c.email}
                                    </span>
                                </div>
                            </td>
                            <td className="px-4 py-3 text-[hsl(var(--text-muted))]">
                                {c.subject || "—"}
                            </td>
                            <td className="px-4 py-3 text-[hsl(var(--text-muted))] max-w-[420px] truncate">
                                {c.message}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ChatsTable = ({ items, onOpen }) => {
    if (!items.length)
        return <EmptyState title="No chat conversations yet" hint="Clap AI conversations will appear here." />;
    return (
        <div className="rounded-2xl border border-black/[0.08] bg-white overflow-hidden shadow-soft">
            <table className="min-w-full text-[13.5px]">
                <thead className="bg-[hsl(var(--bg-2))]/70 text-[hsl(var(--text-subtle))] text-[11px] font-mono uppercase tracking-[0.16em]">
                    <tr>
                        <th className="text-left font-medium px-4 py-3">Last activity</th>
                        <th className="text-left font-medium px-4 py-3">Session</th>
                        <th className="text-left font-medium px-4 py-3">First message</th>
                        <th className="text-left font-medium px-4 py-3">Messages</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((c) => (
                        <tr
                            key={c.session_id}
                            onClick={() => onOpen(c.session_id)}
                            data-testid="admin-chat-row"
                            className="border-t border-black/[0.06] hover:bg-[hsl(var(--bg-2))]/60 cursor-pointer transition-colors"
                        >
                            <td className="px-4 py-3 text-[hsl(var(--text-muted))] whitespace-nowrap">
                                {fmtDate(c.last_activity)}
                            </td>
                            <td className="px-4 py-3 font-mono text-[12px] text-[hsl(var(--text-muted))]">
                                {(c.session_id || "").slice(0, 8)}…
                            </td>
                            <td className="px-4 py-3 text-[hsl(var(--text-muted))] max-w-[420px] truncate">
                                {c.first_message || "(no messages)"}
                            </td>
                            <td className="px-4 py-3 text-[hsl(var(--text))] num-tabular">
                                {c.message_count}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const EmptyState = ({ title, hint }) => (
    <div className="rounded-2xl border border-dashed border-black/15 bg-white/60 p-10 text-center">
        <h3 className="font-display text-xl text-[hsl(var(--text))]">{title}</h3>
        <p className="mt-1.5 text-[13.5px] text-[hsl(var(--text-subtle))]">{hint}</p>
    </div>
);

const DetailDrawer = ({ kind, record, onClose }) => (
    <>
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-[hsl(var(--text))]/35 backdrop-blur-sm"
        />
        <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-y-0 right-0 z-50 w-[min(95vw,540px)] bg-white border-l border-black/[0.08] shadow-deep flex flex-col"
            role="dialog"
            aria-label="Record detail"
            data-testid="admin-detail-drawer"
        >
            <header className="flex items-center justify-between gap-3 p-5 border-b border-black/[0.08]">
                <div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                        {kind === "leads" ? "Project lead" : "Contact message"}
                    </div>
                    <h3 className="font-display text-2xl text-[hsl(var(--text))] leading-tight mt-0.5">
                        {kind === "leads"
                            ? record.email
                            : record.subject || record.name || record.email}
                    </h3>
                </div>
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-black/10 hover:bg-[hsl(var(--bg-2))]"
                >
                    <X className="h-4 w-4" />
                </button>
            </header>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {kind === "leads" ? (
                    <LeadDetail lead={record} />
                ) : (
                    <ContactDetail contact={record} />
                )}
            </div>
        </motion.aside>
    </>
);

const LeadDetail = ({ lead }) => (
    <>
        <FieldRow icon={User} label="Name" value={lead.name || "—"} />
        <FieldRow icon={Mail} label="Email" value={lead.email} link={`mailto:${lead.email}`} />
        <FieldRow icon={Building2} label="Company" value={lead.company || "—"} />
        <FieldRow icon={Calendar} label="Submitted" value={fmtDateLong(lead.created_at)} />
        <FieldRow icon={Clock} label="Timeline" value={lead.timeline || "—"} />
        <FieldRow icon={Wallet} label="Budget" value={lead.budget_range || "—"} />
        <FieldRow
            icon={Tag}
            label="Capabilities"
            value={(lead.capabilities || []).join(" · ") || "—"}
        />
        <FieldRow icon={ArrowLeft} label="Submitted from" value={lead.referrer || "—"} />
        <Block label="What they're building" body={lead.what_building} />
        <Block label="Goals" body={lead.goals} />
    </>
);

const ContactDetail = ({ contact }) => (
    <>
        <FieldRow icon={User} label="Name" value={contact.name || "—"} />
        <FieldRow
            icon={Mail}
            label="Email"
            value={contact.email}
            link={`mailto:${contact.email}`}
        />
        <FieldRow icon={Calendar} label="Submitted" value={fmtDateLong(contact.created_at)} />
        <FieldRow icon={Tag} label="Subject" value={contact.subject || "—"} />
        <Block label="Message" body={contact.message} />
    </>
);

const FieldRow = ({ icon: Icon, label, value, link }) => (
    <div className="flex items-start gap-3">
        <span className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--bg-2))] text-[hsl(var(--text-muted))]">
            <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0">
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text-subtle))]">
                {label}
            </div>
            {link ? (
                <a
                    href={link}
                    className="text-[14.5px] text-[hsl(var(--text))] hover:text-[hsl(var(--gold))] transition-colors break-words"
                >
                    {value}
                </a>
            ) : (
                <div className="text-[14.5px] text-[hsl(var(--text))] break-words">
                    {value}
                </div>
            )}
        </div>
    </div>
);

const Block = ({ label, body }) => (
    <div className="rounded-xl border border-black/[0.08] bg-[hsl(var(--bg-2))] p-4">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text-subtle))]">
            {label}
        </div>
        <p className="mt-1.5 text-[14.5px] leading-relaxed text-[hsl(var(--text))] whitespace-pre-wrap break-words">
            {body || "—"}
        </p>
    </div>
);

const ChatSessionDrawer = ({ sessionId, messages, loading, onClose }) => (
    <>
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-[hsl(var(--text))]/35 backdrop-blur-sm"
        />
        <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-y-0 right-0 z-50 w-[min(95vw,540px)] bg-white border-l border-black/[0.08] shadow-deep flex flex-col"
            role="dialog"
            aria-label="Chat session"
            data-testid="admin-chat-drawer"
        >
            <header className="flex items-center justify-between gap-3 p-5 border-b border-black/[0.08]">
                <div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                        Clap AI session
                    </div>
                    <h3 className="font-display text-2xl text-[hsl(var(--text))] leading-tight mt-0.5 font-mono">
                        {sessionId.slice(0, 8)}…
                    </h3>
                </div>
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-black/10 hover:bg-[hsl(var(--bg-2))]"
                >
                    <X className="h-4 w-4" />
                </button>
            </header>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {loading ? (
                    <div className="flex items-center justify-center py-10 text-[hsl(var(--text-subtle))]">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Loading…
                    </div>
                ) : (messages || []).length === 0 ? (
                    <EmptyState
                        title="No messages found"
                        hint="This session has no recorded messages."
                    />
                ) : (
                    (messages || []).map((m) => {
                        const isUser = m.role === "user";
                        return (
                            <div
                                key={m.id}
                                className={`flex flex-col gap-1 ${
                                    isUser ? "items-end" : "items-start"
                                }`}
                            >
                                <span className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text-subtle))]">
                                    {isUser ? "User" : "Clap AI"} · {fmtDate(m.created_at)}
                                </span>
                                <div
                                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-[1.55] ${
                                        isUser
                                            ? "bg-[hsl(var(--text))] text-[hsl(var(--bg))]"
                                            : "bg-[hsl(var(--bg-2))] text-[hsl(var(--text))] ring-1 ring-black/[0.06]"
                                    }`}
                                >
                                    <span className="whitespace-pre-wrap break-words">{m.content}</span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </motion.aside>
    </>
);

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};
const fmtDateLong = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};
